import { useQuery } from "@tanstack/react-query"

import MultiGauge from "@/components/MultiGauge.tsx"
import { getLiveForHireMetrics } from "@/features/logistics/api/getLiveForHireMetrics.ts"

import { getStatusData } from "../../api/ForHire/getStatusData"
import GaugeCard from "../GaugeCard.tsx"

export default function ForHireGauge({
  thresholds = [952, 1190, 1428, 1570],
}: {
  thresholds?: number[]
}) {
  const {
    data,
    isPending: isRevenuePending,
    isError: isRevenueError,
  } = useQuery({
    queryKey: ["liveForHireMetrics", "Daily"],
    queryFn: () => getLiveForHireMetrics("Daily"),
  })

  const availableTruckQuery = useQuery({
    queryKey: ["forHireStatus"],
    queryFn: getStatusData,
  })

  const statusData = availableTruckQuery.data
  const availableTrucks = statusData ? statusData.available : 0

  // Status text reflects fleet activity only (an independent live signal from
  // the daily revenue accumulator shown on the gauge).
  const statusText = availableTruckQuery.isError
    ? "Error"
    : availableTruckQuery.isPending || !statusData
      ? "N/A"
      : statusData.moving > 0
        ? "Rolling"
        : "Off"

  const statusClass =
    availableTruckQuery.isError || (statusData && statusData.moving === 0)
      ? "text-status-bad"
      : statusData && statusData.moving > 0
        ? "text-metric-accent"
        : "text-muted-foreground"

  // Only null the gauge value when one of the underlying queries hasn't
  // resolved (loading / error). Once both have data, a computed 0 is a real
  // value (e.g. start of day, no revenue yet) and should be shown as "0", not
  // a placeholder.
  const gaugeUnavailable =
    isRevenuePending ||
    isRevenueError ||
    availableTruckQuery.isPending ||
    availableTruckQuery.isError ||
    !data ||
    !statusData
  const gaugeValue = gaugeUnavailable
    ? null
    : availableTrucks > 0
      ? data.dailyRevenue / availableTrucks
      : 0

  return (
    <GaugeCard
      title="Revenue per Truck Today"
      subtitle="($)"
      status={
        <span
          className={`font-semibold text-2xl leading-7 pr-1 font-sans text-right shrink-0 min-w-[6rem] ${statusClass}`}
        >
          {statusText}
        </span>
      }
    >
      <MultiGauge
        thresholds={thresholds}
        colors={[
          "var(--status-bad)",
          "var(--status-warn)",
          "var(--status-good)",
          "var(--metric-accent)",
        ]}
        value={gaugeValue}
        placeholder={isRevenueError || availableTruckQuery.isError ? "Error" : "—"}
        labels={true}
      />
    </GaugeCard>
  )
}
